import api from "./axios";
import type { Camper, PaginatedResponse } from "../types";

/** Server-side session-attendance filter; see Camp Highlands spec §4. */
export type AttendanceFilter = "all" | "session-only" | "full-term";

interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  cabinId?: number;
  year?: number;
  termId?: number;
  divisionId?: number;
  divisionIds?: number[];
  team?: string;
  attendance?: AttendanceFilter;
  includeArchived?: boolean | string;
}

export interface DuplicateWarning {
  warning: "possible_duplicate";
  candidates: {
    id: number;
    first_name: string;
    last_name: string;
    date_of_birth: string | null;
    camp_minder_person_id: number | null;
    completions_count: number;
  }[];
}

export type ConflictPolicy = "keep_winner" | "keep_loser" | "keep_oldest";
export type RowPick = "winner" | "loser" | "drop";
export type FieldPick = "winner" | "loser" | "concat";

export interface CamperFieldSelections {
  firstName?: "winner" | "loser";
  lastName?: "winner" | "loser";
  dateOfBirth?: "winner" | "loser";
  team?: "winner" | "loser";
  campMinderPersonId?: "winner" | "loser";
  excludedFromReports?: "winner" | "loser";
  notes?: FieldPick;
}

export interface MergeSelections {
  fields?: CamperFieldSelections;
  completions?: Record<number, RowPick>;
  enrollments?: Record<string, RowPick>;
  include?: {
    honors?: boolean;
    assessments?: boolean;
    linkedUsers?: boolean;
  };
}

export interface MergeResult {
  winnerId: number;
  loserId: number;
  moved: {
    completions: number;
    honors: number;
    assessments: number;
    enrollments: number;
    users: number;
  };
  conflicts: {
    table: string;
    achievement_credit_id?: number;
    year?: number;
    term_id?: number | null;
    resolution: RowPick | ConflictPolicy;
    dropped_row_id: number;
  }[];
}

export interface PreviewCompletion {
  id: number;
  achievementCreditId: number;
  achievementCreditName: string;
  achievementCreditCode: string;
  status: string;
  completedDate: string | null;
  notes: string | null;
}

export interface PreviewEnrollment {
  id: number;
  key: string;
  year: number;
  termId: number | null;
  termName: string | null;
  termStartDate: string | null;
  termEndDate: string | null;
  cabinId: number | null;
  cabinName: string | null;
  divisionId: number | null;
  divisionName: string | null;
}

export interface PreviewCamper {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  team: "army" | "navy" | null;
  campMinderPersonId: number | null;
  excludedFromReports: boolean;
  notes: string | null;
  archivedAt: string | null;
  completions: PreviewCompletion[];
  enrollments: PreviewEnrollment[];
  honorCount: number;
  assessmentCount: number;
  linkedUserCount: number;
}

export interface MergePreview {
  winner: PreviewCamper;
  loser: PreviewCamper;
  completionConflicts: number[];
  enrollmentConflicts: string[];
  campMinderConflict: boolean;
}

export type DuplicateConfidence = "high" | "medium" | "low";

export interface DuplicateCamperSummary {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  campMinderPersonId: number | null;
  archivedAt: string | null;
  /** Set when the camper's PersonID vanished from CampMinder (merge/delete). */
  cmOrphanedAt: string | null;
  completionCount: number;
  enrollmentCount: number;
}

export interface DuplicateSuggestion {
  camperA: DuplicateCamperSummary;
  camperB: DuplicateCamperSummary;
  confidence: DuplicateConfidence;
  reason: string;
  score: number;
  dismissedAt?: string;
  dismissedById?: number | null;
  dismissedByName?: string | null;
  dismissalId?: number;
}

export interface DuplicateSuggestionsResponse {
  counts: { high: number; medium: number; low: number };
  suggestions: DuplicateSuggestion[];
}

export interface DuplicateListParams {
  confidence?: DuplicateConfidence | "all";
  includeArchived?: boolean;
  includeDismissed?: boolean;
  limit?: number;
}

export const campersApi = {
  list: async (params: ListParams = {}): Promise<PaginatedResponse<Camper>> => {
    const { divisionIds, ...rest } = params;
    const queryParams: Record<string, unknown> = { ...rest };
    if (divisionIds && divisionIds.length > 0) {
      queryParams.divisionIds = divisionIds.join(",");
    }
    const res = await api.get<PaginatedResponse<Camper>>("/campers", {
      params: queryParams,
    });
    return res.data;
  },

  get: async (id: number): Promise<Camper> => {
    const res = await api.get<Camper>(`/campers/${id}`);
    return res.data;
  },

  create: async (
    data: Omit<Camper, "id" | "cabinGroup">,
    opts: { force?: boolean } = {}
  ): Promise<Camper | DuplicateWarning> => {
    const res = await api.post<Camper | DuplicateWarning>(
      "/campers",
      data,
      opts.force ? { params: { force: "true" } } : undefined
    );
    return res.data;
  },

  update: async (
    id: number,
    data: Partial<Omit<Camper, "id" | "cabinGroup">>
  ): Promise<Camper> => {
    const res = await api.put<Camper>(`/campers/${id}`, data);
    return res.data;
  },

  setTeam: async (id: number, team: "army" | "navy" | null): Promise<Camper> => {
    const res = await api.patch<Camper>(`/campers/${id}/team`, { team });
    return res.data;
  },

  archive: async (id: number): Promise<Camper> => {
    const res = await api.patch<Camper>(`/campers/${id}/archive`);
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/campers/${id}`);
  },

  merge: async (
    winnerId: number,
    loserId: number,
    opts: { conflictPolicy?: ConflictPolicy; selections?: MergeSelections } = {}
  ): Promise<MergeResult> => {
    const res = await api.post<MergeResult>(`/campers/${winnerId}/merge`, {
      loserId,
      conflictPolicy: opts.conflictPolicy ?? "keep_winner",
      selections: opts.selections,
    });
    return res.data;
  },

  mergePreview: async (winnerId: number, loserId: number): Promise<MergePreview> => {
    const res = await api.get<MergePreview>(`/campers/${winnerId}/merge/preview`, {
      params: { loserId },
    });
    return res.data;
  },

  duplicates: async (
    params: DuplicateListParams = {}
  ): Promise<DuplicateSuggestionsResponse> => {
    const res = await api.get<DuplicateSuggestionsResponse>("/campers/duplicates", {
      params,
    });
    return res.data;
  },

  dismissDuplicate: async (
    camperAId: number,
    camperBId: number
  ): Promise<{ id: number }> => {
    const res = await api.post<{ id: number }>("/campers/duplicates/dismiss", {
      camperAId,
      camperBId,
    });
    return res.data;
  },

  undismissDuplicate: async (dismissalId: number): Promise<void> => {
    await api.delete(`/campers/duplicates/dismiss/${dismissalId}`);
  },
};
